import { useCallback, useMemo, useRef, useState } from 'react'
import type { JSX } from 'react'

import {
  CriticalIcon,
  DropdownMenu,
  SimpleSpinner,
  Text,
  TrashIcon,
  UploadIcon,
  VisuallyHidden,
  toast,
  useDelayedLoading,
} from '@buffer-mono/popcorn'
import { getHumanReadableSize } from '@buffer-mono/shared-utils/uploads'
import {
  FileUploadConfig,
  UploadSource,
  useUploader,
} from '@buffer-mono/uploader'
import type {
  LocalFile,
  UploaderEvents,
  UploadType,
} from '@buffer-mono/uploader'

import {
  useAccountId,
  useOrganizationId,
} from '~publish/contexts/AccountContext'

import { cropToCanvas } from '../../utils/cropToCanvas'

import styles from './PhotoUploader.module.css'

// Mirrors the real per-format cap so this pre-check never drifts from what
// validateFilesForUploads actually enforces.
const DEFAULT_MAX_FILE_SIZE = Math.max(
  ...Array.from(FileUploadConfig.IMAGE.values()).map(
    (format) => format.maxSize,
  ),
)

const DEFAULT_ALLOWED_FILE_TYPES = ['.jpg', '.jpeg', '.png', '.webp']

export interface PhotoUploaderProps {
  /** Current image URL */
  src?: string
  /** Alt text for the preview image */
  alt: string
  /** Section heading */
  label: string
  /** Helper text below heading */
  description: string
  /** Uploader type identifier */
  uploadType: UploadType
  /**
   * Called with the uploaded URL after successful CDN upload.
   * Throw an Error to display its message as an error.
   */
  onUploadComplete: (url: string) => Promise<void> | void
  /** Called when the remove button is clicked. Only shown when src is set. */
  onRemove?: () => void
  /** Called when upload loading state changes */
  onLoadingChange?: (isLoading: boolean) => void
  /** Max file size in bytes (default: the shared per-format upload cap) */
  maxFileSize?: number
  /** If set, crops image to a square of this pixel size before upload */
  cropSize?: number
  /** Allowed file extensions (default: jpg, jpeg, png, webp) */
  allowedFileTypes?: string[]
  /** Error message for oversized files */
  fileSizeErrorMessage?: string
  /** Error message for CDN upload failure */
  uploadErrorMessage?: string
}

export function PhotoUploader({
  src,
  alt,
  label,
  description,
  uploadType,
  onUploadComplete,
  onRemove,
  onLoadingChange,
  maxFileSize = DEFAULT_MAX_FILE_SIZE,
  cropSize,
  allowedFileTypes = DEFAULT_ALLOWED_FILE_TYPES,
  fileSizeErrorMessage = `File must be under ${getHumanReadableSize(
    maxFileSize,
  )}.`,
  uploadErrorMessage = 'Photo upload failed. Please try again.',
}: PhotoUploaderProps): JSX.Element {
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const showSpinner = useDelayedLoading(isLoading)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const accountId = useAccountId()
  const organizationId = useOrganizationId()

  // Uppy silently drops files that fail its own validation (wrong type, too
  // large): upload() then resolves with an empty successful/failed result
  // and no network request. Surfacing the real rejection message here means
  // the user (and we) see why, instead of a generic fallback.
  const lastValidationErrorRef = useRef<string | null>(null)
  const handleFileValidationFailed = useCallback(
    (_file: LocalFile, validationError: Error): void => {
      lastValidationErrorRef.current = validationError.message
    },
    [],
  )
  const eventHandlers = useMemo<Partial<UploaderEvents>>(
    () => ({ 'file-validation-failed': handleFileValidationFailed }),
    [handleFileValidationFailed],
  )

  // useUploader rebuilds (and tears down) its Uppy instance whenever this
  // object's identity changes. A fresh literal on every render meant any
  // re-render mid-upload (e.g. the setIsLoading below) destroyed the very
  // Uppy instance the in-flight upload() call was about to use.
  const fileRestrictions = useMemo(
    () => ({
      maxNumberOfFiles: 1,
      allowedFileTypes,
      uploadConfig: FileUploadConfig.IMAGE,
    }),
    [allowedFileTypes],
  )

  const uploader = useUploader({
    id: uploadType,
    userId: accountId ?? '',
    organizationId: organizationId ?? '',
    fileRestrictions,
    eventHandlers,
  })

  const updateLoading = (loading: boolean): void => {
    setIsLoading(loading)
    onLoadingChange?.(loading)
  }

  const handleButtonClick = (): void => {
    fileInputRef.current?.click()
  }

  const acceptFileTypes = allowedFileTypes.join(',')

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    updateLoading(true)
    setError('')
    lastValidationErrorRef.current = null

    if (!event.target.files?.length) {
      updateLoading(false)
      return
    }

    const file = event.target.files[0]

    if (file.size > maxFileSize) {
      setError(fileSizeErrorMessage)
      toast.error(fileSizeErrorMessage)
      updateLoading(false)
      uploader.uppyInstance.cancelAll()
      event.target.value = ''
      return
    }

    try {
      let fileToUpload: File | Blob = file

      if (cropSize) {
        const fileAsObjectUrl = URL.createObjectURL(file)

        try {
          const croppedBlob = await cropToCanvas(fileAsObjectUrl, cropSize)
          fileToUpload = croppedBlob ?? file
        } finally {
          URL.revokeObjectURL(fileAsObjectUrl)
        }
      }

      const result = await uploader.upload(
        [
          new File([fileToUpload], file.name, {
            type: fileToUpload.type || file.type,
          }),
        ],
        {
          source: UploadSource.filePicker(),
        },
      )

      if (result.successful.length) {
        try {
          await onUploadComplete(result.successful[0].uploadURL)
          setError('')
        } catch (err) {
          const message =
            err instanceof Error ? err.message : uploadErrorMessage

          setError(message)
          toast.error(message)
        }
      } else {
        const message = lastValidationErrorRef.current ?? uploadErrorMessage
        setError(message)
        toast.error(message)
      }
    } catch {
      setError(uploadErrorMessage)
      toast.error(uploadErrorMessage)
    } finally {
      updateLoading(false)
      uploader.uppyInstance.cancelAll()
      event.target.value = ''
    }
  }

  const thumbnail = (
    <span
      className={styles.thumbnail}
      data-populated={src ? '' : undefined}
      aria-hidden
    >
      {src && <img src={src} alt={alt} className={styles.thumbnailImage} />}
      {showSpinner ? (
        <span className={styles.loadingOverlay}>
          <SimpleSpinner />
        </span>
      ) : src ? (
        <span className={styles.thumbnailOverlay}>
          <UploadIcon />
        </span>
      ) : (
        <UploadIcon color="subtle" />
      )}
    </span>
  )

  const textContent = (
    <span className={styles.textContent}>
      <Text weight="medium">{label}</Text>
      <Text color="subtle" size="sm">
        {description}
      </Text>
    </span>
  )

  return (
    <div className={styles.root}>
      <VisuallyHidden
        as="input"
        type="file"
        ref={fileInputRef}
        aria-label={label}
        accept={acceptFileTypes}
        multiple={false}
        onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
          void handleFileChange(event)
        }}
      />

      {src ? (
        <DropdownMenu>
          <DropdownMenu.Trigger asChild>
            <button
              type="button"
              className={styles.trigger}
              disabled={isLoading}
              aria-busy={isLoading}
              aria-label={`Change ${label.toLowerCase()}`}
            >
              {thumbnail}
              {textContent}
            </button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Content align="start">
            <DropdownMenu.Item onSelect={handleButtonClick}>
              <UploadIcon />
              Upload New
            </DropdownMenu.Item>
            {onRemove && (
              <DropdownMenu.Item
                onSelect={(): void => {
                  onRemove()
                }}
              >
                <TrashIcon />
                Delete
              </DropdownMenu.Item>
            )}
          </DropdownMenu.Content>
        </DropdownMenu>
      ) : (
        <button
          type="button"
          className={styles.trigger}
          onClick={handleButtonClick}
          disabled={isLoading}
          aria-busy={isLoading}
          aria-label={`Upload ${label.toLowerCase()}`}
        >
          {thumbnail}
          {textContent}
        </button>
      )}

      {error && (
        <Text
          size="sm"
          color="critical"
          className={styles.error}
          icon={<CriticalIcon size="xsmall" color="critical" />}
        >
          {error}
        </Text>
      )}
    </div>
  )
}
